import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { LinkIcon, SendIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Input } from '../ui/Field';
import { ReportControls } from '../trust/ReportControls';
import { conversations as seedConversations } from '../../data/community';
import type { Conversation } from '../../types/community';
const KIND_LABEL: Record<Conversation['kind'], string> = {
  person: 'Student',
  group: 'Group',
  provider: 'Provider',
  landlord: 'Property manager'
};
export function Messages() {
  const [conversations, setConversations] = useState<Conversation[]>(seedConversations);
  const [activeId, setActiveId] = useState(seedConversations[0].id);
  const [draft, setDraft] = useState('');
  const active = conversations.find(conversation => conversation.id === activeId);
  function send() {
    if (draft.trim().length === 0 || !active) return;
    setConversations(current => current.map(conversation => conversation.id === active.id ? {
      ...conversation,
      lastAt: 'Just now',
      messages: [...conversation.messages, {
        id: `m-${Date.now()}`,
        from: 'me' as const,
        body: draft.trim(),
        at: 'Just now'
      }]
    } : conversation));
    setDraft('');
  }
  return <div className="grid gap-6 lg:grid-cols-[300px_minmax(0,1fr)]" data-id="element-992" data-mp="2mynqqlhzzibu">
      <div className="rounded-card border border-line bg-surface" data-id="element-993" data-mp="2mwufsu0cqby1">
        <p className="border-b border-line px-5 py-3 text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-994" data-mp="2rwp9f5bihfms">
          Conversations
        </p>
        <ul className="divide-y divide-line" data-id="element-995" data-mp="1za4gi0fe0vh4">
          {conversations.map(conversation => {
          const selected = conversation.id === activeId;
          return <li key={conversation.id} data-id="element-996" data-mp="1w800u2hct9fh">
                <button type="button" onClick={() => {
              setActiveId(conversation.id);
              setConversations(current => current.map(item => item.id === conversation.id ? {
                ...item,
                unread: 0
              } : item));
            }} aria-current={selected} className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors duration-150 ease-out ${selected ? 'bg-brand-50' : 'hover:bg-canvas'}`} data-id="element-997" data-mp="1w6ycwxf8osgc">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-canvas text-[12px] font-semibold text-ink-muted" data-id="element-998" data-mp="1i88g7iwc0usi">
                    {conversation.initials}
                  </span>
                  <span className="min-w-0 flex-1" data-id="element-999" data-mp="2tnx3ychetxud">
                    <span className="flex items-center gap-2" data-id="element-1000" data-mp="2tn5e7iahuuc0">
                      <span className="truncate text-[13px] font-semibold text-ink" data-id="element-1001" data-mp="fel11obkqk2e">
                        {conversation.withName}
                      </span>
                      {conversation.unread > 0 && <span className="cz-num ml-auto shrink-0 rounded-full bg-brand-700 px-1.5 py-0.5 text-[11px] font-semibold text-white" data-id="element-1002" data-mp="6giuyuczh189">
                          {conversation.unread}
                        </span>}
                    </span>
                    <span className="mt-0.5 block truncate text-[12px] text-ink-muted" data-id="element-1003" data-mp="135groi4an982">
                      {conversation.messages[conversation.messages.length - 1]?.body}
                    </span>
                    <span className="cz-num mt-0.5 block text-[11px] text-ink-subtle" data-id="element-1004" data-mp="uc3o8m8lsp7q">
                      {KIND_LABEL[conversation.kind]} · {conversation.lastAt}
                    </span>
                  </span>
                </button>
              </li>;
        })}
        </ul>
      </div>

      {active && <div className="flex min-h-[520px] flex-col rounded-card border border-line bg-surface" data-id="element-1005" data-mp="3uec875cq12pn">
          <div className="flex flex-wrap items-center gap-3 border-b border-line px-5 py-3.5" data-id="element-1006" data-mp="3pc4sc4ort2i1">
            <div className="min-w-0" data-id="element-1007" data-mp="3lcmj466bafv1">
              <p className="text-[14px] font-semibold text-ink" data-id="element-1008" data-mp="3gcztex9ar75c">{active.withName}</p>
              <p className="cz-num text-[12px] text-ink-muted" data-id="element-1009" data-mp="3gf2vwgrmysqy">
                {active.kind === 'group' ? 'Group thread' : `@${active.withHandle}`} ·{' '}
                {KIND_LABEL[active.kind]}
              </p>
            </div>
            <div className="ml-auto flex items-center gap-2" data-id="element-1010" data-mp="8519b91fwkk5">
              <ReportControls subject={active.withName} surface="message" allowBlock={active.kind !== 'group'} data-id="element-1011" data-mp="83gctdvkerjn" />
            </div>
          </div>

          {active.context && <Link to={active.context.to} className="flex items-center gap-2 border-b border-line bg-canvas px-5 py-2.5 text-[12px] text-ink-muted transition-colors duration-150 ease-out hover:text-ink" data-id="element-1012" data-mp="vulsnt1i9jty">
              <LinkIcon className="h-3 w-3 shrink-0" data-id="element-1013" data-mp="185mww907aaey" />
              {active.context.label}
            </Link>}

          <ul className="flex-1 space-y-3 overflow-y-auto px-5 py-4" data-id="element-1014" data-mp="1d6u2c1evexig">
            {active.messages.map(message => <li key={message.id} className={`flex ${message.from === 'me' ? 'justify-end' : 'justify-start'}`} data-id="element-1015" data-mp="1m5fg7j9edocw">
                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${message.from === 'me' ? 'bg-brand-700 text-white' : 'border border-line bg-canvas text-ink'}`} data-id="element-1016" data-mp="1r25xub0uos0f">
                  <p className="text-[13px] leading-6" data-id="element-1017" data-mp="24x897dogv9km">{message.body}</p>
                  <p className={`cz-num mt-1 text-[11px] ${message.from === 'me' ? 'text-white/70' : 'text-ink-subtle'}`} data-id="element-1018" data-mp="29vs0ltltxdkd">
                    {message.at}
                  </p>
                </div>
              </li>)}
          </ul>

          <div className="border-t border-line px-5 py-3" data-id="element-1019" data-mp="jjlejrr5rysw">
            <form className="flex items-center gap-2" onSubmit={event => {
          event.preventDefault();
          send();
        }} data-id="element-1020" data-mp="erbsimt5zivw">
              <Input value={draft} onChange={event => setDraft(event.target.value)} placeholder="Write a message" aria-label={`Message ${active.withName}`} data-id="element-1021" data-mp="oi6h3xtvuuki" />
              <Button type="submit" variant="primary" icon={<SendIcon className="h-3.5 w-3.5" data-id="element-1023" data-mp="3489ka5uep86i" />} data-id="element-1022" data-mp="xiuuqrfd6swv">
                Send
              </Button>
            </form>
            <p className="mt-2 text-[12px] text-ink-subtle" data-id="element-1024" data-mp="2za80fip5p4tz">
              Messaging is shared infrastructure — the same threads carry provider orders, landlord
              maintenance and group coordination, each with its own context.
            </p>
          </div>
        </div>}
    </div>;
}